import { auth, db } from '../config/firebase';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, updateProfile, sendPasswordResetEmail, signInWithPopup, GoogleAuthProvider, deleteUser } from 'firebase/auth';
import { collection, doc, getDoc, query, where, setDoc, getDocs, deleteDoc, addDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { calendarSchedulingService } from './calendarSchedulingService';



export const authService = {
  // Login with email or username and password
  login: async (email, password) => {

    try {
      // Proceed with Firebase Auth login using the email
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Get additional user data from Firestore
      let userData = null;
      
      // First check if user exists in Firestore
      const userRef = doc(db, 'users', userCredential.user.uid);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists) {
        // User exists in Firestore, get their data
        userData = userDoc.data();
      } else  {
        throw new Error('User not found');
      }
      
      return { 
        user: {
          ...userCredential.user,
          ...userData
        }, 
        error: null 
      };
    } catch (error) {
      let errorMessage = 'An error occurred during login';
      
      // Handle specific Firebase auth errors
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'No account found with this email/username';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Incorrect password';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many failed attempts. Please try again later';
          break;
        case 'auth/invalid-credential':
          errorMessage = 'Invalid email/username or password';
          break;
        case 'auth/user-disabled':
          errorMessage = 'This account has been disabled';
          break;
      }
      
      return { user: null, error: errorMessage };
    }
  },

  // Register new user
  register: async (email, password, firstName, lastName, mode, autoUpdate) => {
    try {
      // Create user with email and password first
      // Firebase Auth will handle duplicate email validation
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      
      // Default preferences
      const defaultPreferences = {
        darkMode: mode === 'dark' ? true : false,
        emailNotifications: true,
        autoUpdate: autoUpdate,
        notifications: {
          progressReports: true,
          frequency: 'weekly', // 'daily', 'weekly', 'monthly'
          time: '05:00', // 5:00 AM
          timezone: 'America/Los_Angeles', // PST
          registrationEmails: true
        }
      };

      // Then create the user profile in Firestore with default preferences
      const userData = {
        uid: userCredential.user.uid,
        username: email.split('@')[0], // Default username from email
        email,
        userType: 'user',
        phoneNumber: '',
        profilePhoto: '',
        firstName,
        lastName,
        createdAt: new Date(),
        preferences: defaultPreferences,
      };
      
      const userRef = doc(db, 'users', userCredential.user.uid);
      await setDoc(userRef, userData);

      // Initialize calendar scheduling for the new user
      await calendarSchedulingService.initializeUserSchedule(userCredential.user.uid);

      const updatedUser = await getDoc(userRef);
      
      return { 
        user: {
          ...userCredential.user,
          ...updatedUser.data()
        }, 
        error: null 
      };
    } catch (error) {
      console.error('Registration error:', error);
      let errorMessage = 'An error occurred during registration';
      
      // Handle specific Firebase auth errors
      switch (error.code) {
        case 'auth/email-already-in-use':
          errorMessage = 'An account already exists with this email';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address';
          break;
        case 'auth/weak-password':
          errorMessage = 'Password is too weak';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your connection';
          break;
        default:
          // Check for blocked requests
          if (error.message && error.message.includes('ERR_BLOCKED_BY_CLIENT')) {
            errorMessage = 'Request blocked by browser extension. Please disable ad blockers or try incognito mode.';
          } else if (error.message && error.message.includes('Failed to fetch')) {
            errorMessage = 'Network request failed. Please check your internet connection and try again.';
          } else {
            errorMessage = error.message || 'Registration failed';
          }
      }
      
      return { user: null, error: errorMessage };
    }
  },

  // Logout
  logout: async () => {
    try {
      await auth.signOut();
      return { error: null };
    } catch (error) {
      return { error: error.message };
    }
  },

  // Reset password
  resetPassword: async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return { error: null };
    } catch (error) {
      return { error: error.message };
    }
  },

  // Google Sign-In
  signInWithGoogle: async () => {
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');
      
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      // Check if user exists in Firestore
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      
      let userData;
      if (userDoc.exists()) {
        // User exists, update last login and any new Google data
        userData = userDoc.data();
        await setDoc(userRef, {
          ...userData,
          lastLogin: new Date(),
          displayName: user.displayName || userData.displayName,
          photoURL: user.photoURL || userData.photoURL,
          provider: 'google'
        }, { merge: true });
      } else {
        // Default preferences
        const defaultPreferences = {
          darkMode: true,
          emailNotifications: true,
          autoUpdate: true, // Time-based dark/light mode
        };

        // New user, create profile with Google data
        userData = {
          uid: user.uid,
          username: user.displayName || user.email.split('@')[0],
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          phoneNumber: user.phoneNumber || '',
          createdAt: new Date(),
          lastLogin: new Date(),
          provider: 'google',
          preferences: defaultPreferences,
        };
        
        await setDoc(userRef, userData);
      }
      
      return { 
        user: {
          ...user,
          ...userData
        }, 
        error: null 
      };
    } catch (error) {
      console.error('Google Sign-In error:', error);
      let errorMessage = 'An error occurred during Google Sign-In';
      
      // Handle specific Google Sign-In errors
      switch (error.code) {
        case 'auth/popup-closed-by-user':
          errorMessage = 'Sign-in was cancelled';
          break;
        case 'auth/popup-blocked':
          errorMessage = 'Popup was blocked by browser. Please allow popups and try again';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your connection';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many requests. Please try again later';
          break;
        case 'auth/account-exists-with-different-credential':
          errorMessage = 'An account already exists with this email using a different sign-in method';
          break;
        default:
          // Check for blocked requests
          if (error.message && error.message.includes('ERR_BLOCKED_BY_CLIENT')) {
            errorMessage = 'Request blocked by browser extension. Please disable ad blockers or try incognito mode.';
          } else if (error.message && error.message.includes('Failed to fetch')) {
            errorMessage = 'Network request failed. Please check your internet connection and try again.';
          } else {
            errorMessage = error.message || 'Google Sign-In failed';
          }
      }
      
      return { user: null, error: errorMessage };
    }
  },

  // Delete account and user document
  deleteAccount: async () => {
    try {
      const current = auth.currentUser;
      if (!current) return { error: 'No authenticated user' };

      const uid = current.uid;
      // Best effort: delete Firestore user doc first
      try {
        const userRef = doc(db, 'users', uid);
        await deleteDoc(userRef);
      } catch (e) {
        // ignore; proceed to auth deletion
      }

      await deleteUser(current);
      return { error: null };
    } catch (error) {
      let errorMessage = error?.message || 'Failed to delete account';
      // Common: requires recent login
      if (error?.code === 'auth/requires-recent-login') {
        errorMessage = 'Please reauthenticate and try again.';
      }
      return { error: errorMessage };
    }
  },

  // Save email capture for coming soon list
  saveEmailCapture: async (email) => {
    try {
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!email || !emailRegex.test(email)) {
        return { id: null, error: 'Please enter a valid email address' };
      }

      const ref = await addDoc(collection(db, 'emailCaptures'), {
        email,
        createdAt: serverTimestamp(),
      });

      return { id: ref.id, error: null };
    } catch (error) {
      console.error('Error saving email capture:', error);
      let errorMessage = 'Failed to save email. Please try again.';
      
      // Handle specific errors
      if (error.code === 'permission-denied') {
        errorMessage = 'Permission denied. Please contact support.';
      } else if (error.message && error.message.includes('Failed to fetch')) {
        errorMessage = 'Network error. Please check your connection.';
      }
      
      return { id: null, error: errorMessage };
    }
  },

  // Update user commitment level from intake form
  updateUserCommitment: async (userId, commitmentLevel) => {
    try {
      console.log('Updating user commitment:', { userId, commitmentLevel });
      
      const userRef = doc(db, 'users', userId);
      await setDoc(userRef, {
        commitmentLevel,
        hasCompletedIntake: true,
        intakeCompletedAt: new Date(),
      }, { merge: true });

      console.log('User commitment updated successfully');
      return { success: true, error: null };
    } catch (error) {
      console.error('Error updating user commitment:', error);
      
      let errorMessage = error.message;
      if (error.code === 'permission-denied') {
        errorMessage = 'Permission denied. Please ensure you are logged in and try again. If the problem persists, contact support.';
      } else if (error.code === 'unavailable') {
        errorMessage = 'Service temporarily unavailable. Please try again in a moment.';
      } else if (error.code === 'unauthenticated') {
        errorMessage = 'Authentication required. Please log in and try again.';
      }
      
      return { success: false, error: errorMessage };
    }
  },
}; 